/** Existing root utility implementations; kept separate so index.ts is a pure export barrel. */
import type { SirsPriorityInput } from '@statura/types';
import {
  AN_ACC_BASE_PRICE_DAILY,
  AN_ACC_BCT_CATEGORIES,
  AN_ACC_RESPITE_CLASS_DAILY_RATES,
  AN_ACC_CLASS_DAILY_RATES,
  AN_ACC_CLASS_NWAU,
  SAH_CARE_MANAGEMENT_RATE,
} from './regulatory-rates';

function randomUuid(): string {
  const cryptoObject = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (typeof cryptoObject?.randomUUID === 'function') {
    return cryptoObject.randomUUID();
  }

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (character) => {
    const randomNibble = Math.floor(Math.random() * 16);
    const value = character === 'x' ? randomNibble : (randomNibble & 0x3) | 0x8;
    return value.toString(16);
  });
}
import { DEFAULT_TZ, dayBoundsInTz, getMonthInTz, getYearInTz } from './timezone';
import { localDateTimeToUtcIso } from './local-datetime';

// ── Date utilities ──
export function formatAustralianDate(date: Date, timezone: string = DEFAULT_TZ): string {
  return date.toLocaleDateString('en-AU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: timezone,
  });
}

export function formatAustralianDateTime(date: Date, timezone: string = DEFAULT_TZ): string {
  return date.toLocaleString('en-AU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZone: timezone,
  });
}

export function getFinancialYear(
  date: Date,
  timezone: string = DEFAULT_TZ,
): { start: Date; end: Date; label: string } {
  const month = getMonthInTz(date, timezone);
  const fullYear = getYearInTz(date, timezone);
  const year = month >= 6 ? fullYear : fullYear - 1;
  return {
    start: new Date(dayBoundsInTz(`${year}-07-01`, timezone).startUtc),
    end: new Date(dayBoundsInTz(`${year + 1}-06-30`, timezone).endUtc),
    label: `${year}/${year + 1}`,
  };
}

export function getQuarter(
  date: Date,
  timezone: string = DEFAULT_TZ,
): { quarter: number; start: Date; end: Date } {
  const month = getMonthInTz(date, timezone);
  const fullYear = getYearInTz(date, timezone);
  const calendarQuarter = Math.floor(month / 3);
  const startMonth = calendarQuarter * 3 + 1;
  const startYmd = `${fullYear}-${String(startMonth).padStart(2, '0')}-01`;
  const nextYmd =
    startMonth === 10
      ? `${fullYear + 1}-01-01`
      : `${fullYear}-${String(startMonth + 3).padStart(2, '0')}-01`;
  return {
    quarter: ((calendarQuarter + 2) % 4) + 1,
    start: new Date(dayBoundsInTz(startYmd, timezone).startUtc),
    end: new Date(Date.parse(dayBoundsInTz(nextYmd, timezone).startUtc) - 1),
  };
}

// ── Currency ──
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(amount);
}

export function formatCurrencyCompact(amount: number): string {
  if (amount === 0) return '$0';
  // eslint-disable-next-line statura/no-floating-point-money -- canonical SAH/SCHADS/residential regulatory calc, validated against FWC pay guide / Services Australia
  if (amount >= 1000000) return `$${(amount / 1000000).toFixed(1)}M`;
  // eslint-disable-next-line statura/no-floating-point-money -- canonical SAH/SCHADS/residential regulatory calc, validated against FWC pay guide / Services Australia
  if (amount >= 1000) return `$${(amount / 1000).toFixed(1)}K`;
  return formatCurrency(amount);
}

export const AN_ACC_CURRENT_RATE_PERIOD_ID = '2026-10-01' as const;

/**
 * AN-ACC rate periods. The current period reads the SSOT constants in
 * award-data/an-acc.ts; a superseded period keeps its published amounts here so
 * a day in that period is still priced at its own rate.
 *
 * 2025-10-01: national efficient price $295.64 (doh-an-acc-funding-guide-2026-03.pdf, Table 2).
 * 2026-04-01: only standard MM1 BCT changes: 0.50/$147.82 to 0.387/$114.41
 *   (funding guide Tables 4-5, pp 50-51; September 2026 subsidy schedule p 2).
 * 2026-10-01: national efficient price $303.19
 *   (doh-schedule-subsidies-supplements-residential-2026-10-01.pdf, pages 1-2).
 */
const AN_ACC_OCTOBER_2025_RATE_PERIOD = {
  rate_period_id: '2025-10-01',
  effective_from: '2025-10-01',
  effective_to: '2026-03-31',
  base_price_daily: 295.64,
  class_daily_rates: {
    1: 215.82,
    2: 62.08,
    3: 118.26,
    4: 85.74,
    5: 127.13,
    6: 115.3,
    7: 159.65,
    8: 177.38,
    9: 156.69,
    10: 174.43,
    11: 201.04,
    12: 195.12,
    13: 215.82,
  } as Record<number, number>,
  respite_class_daily_rates: { 1: 119.73, 2: 169.7, 3: 211.09 } as Record<number, number>,
  bct_categories: {
    STANDARD_MM1: { ...AN_ACC_BCT_CATEGORIES.STANDARD_MM1, nwau: 0.5, dailyPerBed: 147.82 },
    STANDARD_MM2_3: { ...AN_ACC_BCT_CATEGORIES.STANDARD_MM2_3, dailyPerBed: 156.69 },
    STANDARD_MM4_5: { ...AN_ACC_BCT_CATEGORIES.STANDARD_MM4_5, dailyPerBed: 171.47 },
    SPECIALISED_HOMELESS: { ...AN_ACC_BCT_CATEGORIES.SPECIALISED_HOMELESS, dailyPerBed: 271.99 },
    STANDARD_MM6_7_FIRST29: {
      ...AN_ACC_BCT_CATEGORIES.STANDARD_MM6_7_FIRST29,
      dailyPerBed: 201.04,
    },
    STANDARD_MM6_7_PLACES30PLUS: {
      ...AN_ACC_BCT_CATEGORIES.STANDARD_MM6_7_PLACES30PLUS,
      dailyPerBed: 153.73,
    },
    SPECIALISED_ATSI_MM6: { ...AN_ACC_BCT_CATEGORIES.SPECIALISED_ATSI_MM6, dailyPerBed: 230.6 },
    SPECIALISED_ATSI_MM7: { ...AN_ACC_BCT_CATEGORIES.SPECIALISED_ATSI_MM7, dailyPerBed: 532.15 },
  },
  class_nwau: AN_ACC_CLASS_NWAU,
} as const;

export const AN_ACC_RATE_PERIODS = {
  '2025-10-01': AN_ACC_OCTOBER_2025_RATE_PERIOD,
  '2026-04-01': {
    ...AN_ACC_OCTOBER_2025_RATE_PERIOD,
    rate_period_id: '2026-04-01',
    effective_from: '2026-04-01',
    effective_to: '2026-09-30',
    bct_categories: {
      ...AN_ACC_OCTOBER_2025_RATE_PERIOD.bct_categories,
      STANDARD_MM1: { ...AN_ACC_BCT_CATEGORIES.STANDARD_MM1, dailyPerBed: 114.41 },
    },
  },
  [AN_ACC_CURRENT_RATE_PERIOD_ID]: {
    rate_period_id: AN_ACC_CURRENT_RATE_PERIOD_ID,
    effective_from: '2026-10-01',
    effective_to: null,
    base_price_daily: AN_ACC_BASE_PRICE_DAILY,
    class_daily_rates: AN_ACC_CLASS_DAILY_RATES,
    respite_class_daily_rates: AN_ACC_RESPITE_CLASS_DAILY_RATES,
    bct_categories: AN_ACC_BCT_CATEGORIES,
    class_nwau: AN_ACC_CLASS_NWAU,
  },
} as const;

export type AnAccRatePeriodId = keyof typeof AN_ACC_RATE_PERIODS;

/**
 * The AN-ACC rate period in force on a day (YYYY-MM-DD). Throws for a day
 * before the earliest period held here rather than price it at a later rate.
 */
export function anAccRatePeriodIdForDate(date: string): AnAccRatePeriodId {
  const day = String(date ?? '').slice(0, 10);
  const match = (Object.keys(AN_ACC_RATE_PERIODS) as AnAccRatePeriodId[])
    .sort()
    .reverse()
    .find((id) => AN_ACC_RATE_PERIODS[id].effective_from <= day);
  const parsed = new Date(`${day}T00:00:00Z`);
  if (
    !/^\d{4}-\d{2}-\d{2}$/.test(day) ||
    !Number.isFinite(parsed.getTime()) ||
    parsed.toISOString() !== `${day}T00:00:00.000Z` ||
    !match
  ) {
    throw new RangeError(`No AN-ACC rate period covers ${date}`);
  }
  return match;
}

/** All AN-ACC amounts in force on a day, including the fixed and respite components. */
export function anAccRatesForDate(date: string) {
  const ratePeriodId = anAccRatePeriodIdForDate(date);
  const period = AN_ACC_RATE_PERIODS[ratePeriodId];
  return {
    ratePeriodId,
    basePriceDaily: period.base_price_daily,
    classDailyRates: period.class_daily_rates,
    respiteClassDailyRates: period.respite_class_daily_rates,
    bctCategories: period.bct_categories,
  };
}

export interface AnAccDailyFunding {
  class_code: number;
  rate_period_id: AnAccRatePeriodId;
  effective_from: string;
  base_price_daily: number;
  nwau: number;
  calculated_daily_rate: number;
  daily_rate: number;
}

export function calculateAnAccDailyFunding(
  class_code: number | string,
  rate_period_id: AnAccRatePeriodId = AN_ACC_CURRENT_RATE_PERIOD_ID,
): AnAccDailyFunding {
  const period = AN_ACC_RATE_PERIODS[rate_period_id];
  if (!period) {
    throw new RangeError(`Unsupported AN-ACC rate period: ${rate_period_id}`);
  }

  const classCode =
    typeof class_code === 'string' && class_code.trim() !== ''
      ? Number(class_code)
      : Number(class_code);

  if (!Number.isInteger(classCode) || classCode < 1 || classCode > 13) {
    throw new RangeError('AN-ACC class_code must be an integer from 1 to 13');
  }

  const dailyRate = period.class_daily_rates[classCode];
  const nwau = period.class_nwau[classCode];
  if (dailyRate === undefined || nwau === undefined) {
    throw new RangeError(`AN-ACC class_code ${classCode} is not funded for ${rate_period_id}`);
  }

  // eslint-disable-next-line statura/no-floating-point-money -- AN-ACC class subsidies are published as rounded cents from base NWAU.
  const calculatedDailyRate = Math.round(period.base_price_daily * nwau * 100) / 100;

  return {
    class_code: classCode,
    rate_period_id,
    effective_from: period.effective_from,
    base_price_daily: period.base_price_daily,
    nwau,
    calculated_daily_rate: calculatedDailyRate,
    daily_rate: dailyRate,
  };
}

// ── Name formatting ──
export function getFullName(firstName: string, lastName: string): string {
  return `${firstName} ${lastName}`;
}

export function getInitials(firstName: string, lastName: string): string {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
}

export function getDisplayName(
  firstName: string,
  lastName: string,
  preferredName?: string,
): string {
  return preferredName ? `${preferredName} ${lastName}` : `${firstName} ${lastName}`;
}

// ── ID formatting ──
export function formatMacId(macId: string): string {
  // Format: AC12345678
  return macId.startsWith('AC') ? macId : `AC${macId}`;
}

export function validateMacId(macId: string): boolean {
  return /^AC\d{8}$/.test(macId);
}

export function formatIhi(ihi: string): string {
  // Format: 8003 6000 0000 0000
  const clean = ihi.replace(/\s/g, '');
  return clean.replace(/(\d{4})/g, '$1 ').trim();
}

// ── SIRS ──

/**
 * The 8 SIRS-reportable incident categories under the Aged Care Act 2024 s 16
 * ("meaning of reportable incident") and Aged Care Rules 2025 Ch 5 Pt 3.
 *
 * There is NO 9th "other serious" catch-all category in the Act. The previous
 * implementation incorrectly included `OTHER_SERIOUS` as a 9th type — removed
 * 2026-04-13 per legislative remediation. Historical incidents stored with
 * `OTHER_SERIOUS` in the DB are preserved but flagged for compliance review.
 *
 * Single canonical spelling per category (no duplicate aliases). If a caller
 * passes a non-canonical form, use `normalizeSirsType()` before comparison.
 *
 * Source: Aged Care Act 2024 s 16(1)(a)–(h), verified 2026-04-13.
 */
const SIRS_REPORTABLE_TYPES = [
  'UNREASONABLE_USE_OF_FORCE',
  'UNLAWFUL_SEXUAL_CONTACT',
  'PSYCHOLOGICAL_EMOTIONAL_ABUSE',
  'STEALING_FINANCIAL_COERCION',
  'NEGLECT',
  'INAPPROPRIATE_RESTRICTIVE_PRACTICES',
  'UNEXPLAINED_ABSENCE',
  'UNEXPECTED_DEATH',
] as const;

/** Exported for test assertions and UI filtering. */
export { SIRS_REPORTABLE_TYPES };

/**
 * Category -> the Aged Care Act 2024 s 16(1) paragraph that defines it.
 *
 * This map exists because the array above is NOT in Act order, and people kept deriving the
 * paragraph letter from the array INDEX. Everything from index 3 down is therefore off by
 * one, and `s 16(1)(f)` — which is NEGLECT — was written into ~12 places as the authority for
 * restrictive practices, which is actually (g).
 *
 * s 16(1) in Act order, verified against Compilation No. 2 (1 July 2026):
 *   (a) unreasonable use of force
 *   (b) unlawful sexual contact, or inappropriate sexual conduct
 *   (c) psychological or emotional abuse
 *   (d) unexpected death
 *   (e) stealing from, or financial coercion of, the individual by an aged care worker
 *   (f) neglect
 *   (g) use of a restrictive practice (other than in accordance with the rules)
 *   (h) unexplained absence
 *
 * Never letter these by position. Cite through this map, or cite the range s 16(1)(a)-(h).
 */
export const SIRS_ACT_PARAGRAPH: Record<(typeof SIRS_REPORTABLE_TYPES)[number], string> = {
  UNREASONABLE_USE_OF_FORCE: 'a',
  UNLAWFUL_SEXUAL_CONTACT: 'b',
  PSYCHOLOGICAL_EMOTIONAL_ABUSE: 'c',
  UNEXPECTED_DEATH: 'd',
  STEALING_FINANCIAL_COERCION: 'e',
  NEGLECT: 'f',
  INAPPROPRIATE_RESTRICTIVE_PRACTICES: 'g',
  UNEXPLAINED_ABSENCE: 'h',
};

/** Full citation for a SIRS category, e.g. "Aged Care Act 2024 s 16(1)(g)". */
export function sirsActCitation(type: (typeof SIRS_REPORTABLE_TYPES)[number]): string {
  return `Aged Care Act 2024 s 16(1)(${SIRS_ACT_PARAGRAPH[type]})`;
}

/**
 * Historical / deprecated incident type values that may exist in the database
 * from before the 2026-04-13 legislative remediation. These are NOT valid
 * SIRS categories but must remain queryable for historical record display.
 */
export const SIRS_DEPRECATED_TYPES = ['OTHER_SERIOUS', 'SERIOUS_HARM'] as const;

/**
 * SIRS-reportable types that are ALWAYS Priority 1 regardless of outcome
 * fields, under Aged Care Rules 2025 r 165A-25(2)(c)–(d).
 * Verified: docs/operations/clause-verifications/compliance-only-duties-2026-09-30.md.
 */
const ALWAYS_PRIORITY_1 = ['UNLAWFUL_SEXUAL_CONTACT', 'UNEXPECTED_DEATH', 'UNEXPLAINED_ABSENCE'];

/**
 * Determines SIRS priority for a reportable incident.
 *
 * Accepts either the new SirsPriorityInput object (with injury/police fields) or
 * the legacy (type, severity) signature for backward compatibility.
 *
 * Priority 1 criteria (Aged Care Rules 2025 Ch 5 Pt 3, ACQSC guidance):
 *   1. Always-P1 types: unlawful sexual contact, unexpected death, unexplained absence
 *   2. Reasonable grounds to report to police, whether or not police were notified
 *   3. Injury requiring medical or psychological treatment
 *
 * Priority 2: all other SIRS-reportable incidents.
 *
 * Non-SIRS types always return P2 — the caller must check `isSirsReportable()`
 * first to determine whether the incident is reportable at all.
 *
 * REMOVED 2026-04-13:
 *   - Severity fallback (HIGH/CRITICAL → P1): not a legal basis for priority
 *   - Auto-reportability from injury+medical regardless of type: a non-SIRS
 *     incident does not become reportable solely because of injury outcome
 */
export type SirsTriageFact = 'YES' | 'NO' | 'UNKNOWN';

export type SirsTriageInput = SirsPriorityInput & {
  policeReasonableGrounds?: SirsTriageFact;
  treatmentRequired?: SirsTriageFact;
};

/** Unknown facts open a provisional triage target, not a legal P1 finding. */
export function evaluateSirsPriorityFacts(input: SirsTriageInput): {
  priority: 1 | 2 | null;
  provisional: boolean;
} {
  if (!isSirsReportable(input.type)) return { priority: null, provisional: false };
  if (ALWAYS_PRIORITY_1.includes(input.type)) return { priority: 1, provisional: false };
  const grounds = input.policeReasonableGrounds ?? 'UNKNOWN';
  const treatment = input.treatmentRequired ?? (input.medicalAttentionRequired ? 'YES' : 'UNKNOWN');
  if (grounds === 'YES' || treatment === 'YES') return { priority: 1, provisional: false };
  if (grounds === 'UNKNOWN' || treatment === 'UNKNOWN') {
    return { priority: null, provisional: true };
  }
  return { priority: 2, provisional: false };
}

export function determineSirsPriority(
  typeOrInput: string | SirsTriageInput,
  severity?: string,
): 1 | 2 {
  // Normalize input — support both legacy (type, severity) and new object form
  const input: SirsTriageInput =
    typeof typeOrInput === 'string'
      ? { type: typeOrInput, severity: severity || 'LOW' }
      : typeOrInput;

  // Not a SIRS-reportable type → P2 (caller should have checked isSirsReportable)
  if (!SIRS_REPORTABLE_TYPES.includes(input.type as (typeof SIRS_REPORTABLE_TYPES)[number]))
    return 2;

  // Legacy callers need a numeric preview. Use evaluateSirsPriorityFacts when
  // the caller must distinguish an unknown fact from an assessed P2.
  return evaluateSirsPriorityFacts(input).priority ?? 2;
}

/**
 * Determines whether an incident is reportable under the SIRS scheme.
 *
 * An incident is SIRS-reportable if and only if its type is one of the 8
 * categories defined in Aged Care Act 2024 s 16(1)(a)–(h).
 *
 * REMOVED 2026-04-13: the previous implementation also treated any incident
 * with `injurySustained && medicalAttentionRequired` as auto-reportable
 * regardless of type. This was legally wrong — a fall causing injury is NOT
 * automatically a SIRS reportable incident unless it was caused by one of the
 * 8 named categories (e.g., neglect).
 *
 * The `injurySustained` and `medicalAttentionRequired` parameters are retained
 * in the signature for backward compatibility but are no longer used. They will
 * be removed in a future breaking change.
 */
export function isSirsReportable(
  type: string,
  _injurySustained?: boolean,
  _medicalAttentionRequired?: boolean,
): boolean {
  return SIRS_REPORTABLE_TYPES.includes(type as (typeof SIRS_REPORTABLE_TYPES)[number]);
}

/**
 * Resolves the components of a Date in a given Australian timezone.
 * Uses Intl.DateTimeFormat to correctly handle DST transitions.
 */
function getTimezoneParts(
  date: Date,
  tz: string = DEFAULT_TZ,
): {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
} {
  const formatter = new Intl.DateTimeFormat('en-AU', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  const parts = formatter.formatToParts(date);
  const get = (type: string) => parseInt(parts.find((p) => p.type === type)?.value ?? '0', 10);
  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    hour: get('hour') === 24 ? 0 : get('hour'), // Intl may return 24 for midnight
    minute: get('minute'),
    second: get('second'),
  };
}

/**
 * Creates a Date (UTC instant) from local components in a given timezone.
 * Computes the correct UTC offset by comparing against Intl output.
 */
function dateFromTimezoneParts(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  second: number,
  tz: string = DEFAULT_TZ,
): Date {
  // Normalise calendar overflow (e.g. discovery day + 30) as date components,
  // then resolve the offset at the target wall time, never a guessed instant.
  const calendar = new Date(Date.UTC(year, month - 1, day, hour, minute, second));
  const wall = `${String(calendar.getUTCFullYear()).padStart(4, '0')}-${String(calendar.getUTCMonth() + 1).padStart(2, '0')}-${String(calendar.getUTCDate()).padStart(2, '0')}T${String(calendar.getUTCHours()).padStart(2, '0')}:${String(calendar.getUTCMinutes()).padStart(2, '0')}:${String(calendar.getUTCSeconds()).padStart(2, '0')}`;
  // Calendar deadlines use the earliest fold and move forward through a gap;
  // this preserves the existing calendar-deadline contract, not capture policy.
  const instant = localDateTimeToUtcIso(wall, tz, { disambiguation: 'compatible' });
  if (!instant) throw new Error('Invalid calendar deadline or timezone');
  return new Date(instant);
}

/**
 * SIRS final-report deadline, in days from the initial Commission notification.
 *
 * Set by Rule 165A-45(2)(a) of the Aged Care Rules 2025 (F2025L01173), made
 * under s 165A of the Aged Care Act 2024. The obligation only attaches when
 * the Commissioner requests a final report (rule 165A-45(1)); the Commissioner
 * may also specify an alternative period under rule 165A-45(2)(b).
 *
 * NOTE: Earlier versions of this platform used 60 days, carried over from the
 * pre-1-Nov-2025 Quality of Care Principles 2014 regime. That figure is no
 * longer the law and must not be reintroduced.
 *
 * Verified: docs/operations/clause-verifications/compliance-only-duties-2026-09-30.md.
 * Count tenant-local calendar days from the first notice, not elapsed hours.
 * The statutory period ends with the 84th day; the platform deliberately
 * targets the same local time of day as the notice on that day, which is never
 * later than the statutory end and matches the stored database deadline
 * (Paul, 8 Oct 2026).
 */
export const SIRS_FINAL_REPORT_DAYS = 84;

/**
 * Calculates the SIRS final-report deadline from the day the initial
 * reportable-incident notice was given to the Commissioner. Returns null if
 * the incident does not have a final-report obligation (i.e. the Commissioner
 * has not requested a final report — rule 165A-45(1)).
 *
 * Commissioner-specified extensions under rule 165A-45(2)(b) are modelled at
 * the data layer (`incidents.final_report_deadline_override`) and are not
 * computed here — use COALESCE(override, this) when reading the effective
 * deadline.
 */
export function calculateSirsFinalReportDeadline(
  commissionNotificationDate: Date | string | null | undefined,
  finalReportRequired: boolean,
  timezone: string = DEFAULT_TZ,
): Date | null {
  if (!finalReportRequired || !commissionNotificationDate) return null;
  const base =
    typeof commissionNotificationDate === 'string'
      ? new Date(commissionNotificationDate)
      : commissionNotificationDate;
  if (!Number.isFinite(base.getTime())) return null;
  // Same local wall time on the 84th calendar day, as public.sirs_calendar_deadline
  // stores it (earliest instant in a fold, forward through a gap). The wall clock
  // resolves to whole seconds; carry the notice's milliseconds so the result
  // equals the stored database instant exactly (no transition is sub-second).
  const local = getTimezoneParts(base, timezone);
  const due = dateFromTimezoneParts(
    local.year,
    local.month,
    local.day + SIRS_FINAL_REPORT_DAYS,
    local.hour,
    local.minute,
    local.second,
    timezone,
  );
  return new Date(due.getTime() + base.getUTCMilliseconds());
}

/**
 * Calculates the SIRS reporting deadline in the provider's timezone.
 *
 * - Priority 1: 24 hours from discovery (exact hour-based addition)
 * - Priority 2: 30 calendar days from discovery (date-based addition preserving wall-clock time)
 *
 * Correctly handles daylight saving transitions by computing date/time
 * arithmetic in the specified timezone via Intl.DateTimeFormat.
 *
 * @param timezone - IANA timezone string (e.g. 'Australia/Perth'). Defaults to Sydney.
 */
export function calculateSirsDeadline(
  discoveredAt: Date,
  priority: 1 | 2,
  timezone: string = DEFAULT_TZ,
): Date {
  const local = getTimezoneParts(discoveredAt, timezone);

  if (priority === 1) {
    // Priority 1: add exactly 24 hours (not 1 calendar day — regulatory requirement)
    return new Date(discoveredAt.getTime() + 24 * 60 * 60 * 1000);
  } else {
    // Priority 2: add 30 calendar days preserving wall-clock time across DST,
    // carrying milliseconds so it equals public.sirs_calendar_deadline exactly.
    const due = dateFromTimezoneParts(
      local.year,
      local.month,
      local.day + 30,
      local.hour,
      local.minute,
      local.second,
      timezone,
    );
    return new Date(due.getTime() + discoveredAt.getUTCMilliseconds());
  }
}

/**
 * Returns today's date as YYYY-MM-DD in Australia/Sydney timezone.
 * @deprecated Use todayLocal(tz) from apps/web/src/lib/dates.ts instead
 */
export function todaySydney(): string {
  return new Date().toLocaleDateString('en-CA', { timeZone: DEFAULT_TZ });
}

// ── Budget ──

/**
 * Calculate the standard SAH quarterly carry-over amount.
 *
 * This applies the standard cap ($1,000 or 10% of quarterly budget, whichever
 * is higher) per DOH SAH rules. It does NOT apply to legacy HCP carry-forward
 * funds — those are tracked separately and have no cap.
 *
 * For the canonical carry-over function with full return shape, prefer
 * `calculateSahCarryOver()` from `regulatory-rates.ts`.
 *
 * @deprecated Use calculateSahCarryOver from regulatory-rates.ts for new code.
 */
export function calculateCarryOver(quarterlyBudget: number, budgetRemaining: number): number {
  // No carry-over if budget is overspent (negative remaining)
  if (budgetRemaining <= 0) return 0;
  // eslint-disable-next-line statura/no-floating-point-money -- canonical SAH/SCHADS/residential regulatory calc, validated against FWC pay guide / Services Australia
  const maxCarryOver = Math.max(1000, quarterlyBudget * 0.1);
  return Math.min(budgetRemaining, maxCarryOver);
}

/**
 * Legacy HCP carry-forward funds are exempt from the standard quarterly
 * carry-over cap. They draw down independently from the SAH budget.
 *
 * This function returns the full legacy HCP balance without any cap applied.
 * The standard cap logic in `calculateSahCarryOver` must NOT be applied to
 * this bucket.
 *
 * Source: DOH "Transitioning from Home Care Packages to Support at Home"
 * fact sheet — retained unspent HCP funds are not capped.
 */
export function calculateHcpLegacyCarryForward(
  hcpLegacyBalance: number,
  spentFromLegacy: number,
): number {
  const remaining = hcpLegacyBalance - spentFromLegacy;
  return Math.max(0, Math.round(remaining * 100) / 100);
}

export function calculateCareManagementBudget(
  quarterlyBudget: number,
  rate: number = SAH_CARE_MANAGEMENT_RATE,
): number {
  // SAH care management is HARD-CAPPED at 10% (Aged Care Rules 2025 Ch 4).
  // Previously the cap was incorrectly 15% — corrected 2026-04-07 per legislative review.
  const effectiveRate = Math.min(rate, SAH_CARE_MANAGEMENT_RATE);
  // eslint-disable-next-line statura/no-floating-point-money -- canonical SAH/SCHADS/residential regulatory calc, validated against FWC pay guide / Services Australia
  return quarterlyBudget * effectiveRate;
}

// ── Pagination ──
export function calculatePagination(total: number, page: number, limit: number) {
  return {
    page,
    limit,
    total,
    // eslint-disable-next-line statura/no-floating-point-money -- canonical SAH/SCHADS/residential regulatory calc, validated against FWC pay guide / Services Australia
    totalPages: Math.ceil(total / limit),
    hasNext: page * limit < total,
    hasPrev: page > 1,
  };
}

// ── Slug/ID generation ──
export function generateInvoiceNumber(
  date: Date,
  sequence: number,
  timezone: string = DEFAULT_TZ,
): string {
  const year = getYearInTz(date, timezone);
  return `INV-${year}-${String(sequence).padStart(5, '0')}`;
}

export function generateClaimReference(): string {
  return `CLM-${Date.now()}-${randomUuid().slice(0, 8).toUpperCase()}`;
}

// ── Assessment Scoring Algorithms ──

/** MMSE (Mini-Mental State Examination) — Score 0-30 */
export function scoreMmse(totalScore: number): { interpretation: string; riskLevel: string } {
  if (totalScore >= 24) return { interpretation: 'Normal cognition', riskLevel: 'low' };
  if (totalScore >= 19)
    return { interpretation: 'Mild cognitive impairment', riskLevel: 'moderate' };
  if (totalScore >= 10)
    return { interpretation: 'Moderate cognitive impairment', riskLevel: 'high' };
  return { interpretation: 'Severe cognitive impairment', riskLevel: 'severe' };
}

/** RUDAS (Rowland Universal Dementia Assessment Scale) — Score 0-30, cutoff ≤22 */
export function scoreRudas(totalScore: number): { interpretation: string; riskLevel: string } {
  if (totalScore > 22) return { interpretation: 'Normal cognition', riskLevel: 'low' };
  if (totalScore >= 18)
    return { interpretation: 'Mild cognitive impairment', riskLevel: 'moderate' };
  if (totalScore >= 10)
    return { interpretation: 'Moderate cognitive impairment', riskLevel: 'high' };
  return { interpretation: 'Severe cognitive impairment', riskLevel: 'severe' };
}

/** MoCA (Montreal Cognitive Assessment) — Score 0-30, cutoff ≤25 */
export function scoreMoca(totalScore: number): { interpretation: string; riskLevel: string } {
  if (totalScore >= 26) return { interpretation: 'Normal cognition', riskLevel: 'low' };
  if (totalScore >= 18)
    return { interpretation: 'Mild cognitive impairment', riskLevel: 'moderate' };
  if (totalScore >= 10)
    return { interpretation: 'Moderate cognitive impairment', riskLevel: 'high' };
  return { interpretation: 'Severe cognitive impairment', riskLevel: 'severe' };
}

/** Braden Scale — Score 6-23. Lower = higher risk */
export function scoreBraden(subscales: {
  sensoryPerception: number;
  moisture: number;
  activity: number;
  mobility: number;
  nutrition: number;
  frictionShear: number;
}): { totalScore: number; interpretation: string; riskLevel: string } {
  const total =
    subscales.sensoryPerception +
    subscales.moisture +
    subscales.activity +
    subscales.mobility +
    subscales.nutrition +
    subscales.frictionShear;
  if (total <= 9)
    return {
      totalScore: total,
      interpretation: 'Very high risk of pressure injury',
      riskLevel: 'severe',
    };
  if (total <= 12)
    return { totalScore: total, interpretation: 'High risk of pressure injury', riskLevel: 'high' };
  if (total <= 14)
    return {
      totalScore: total,
      interpretation: 'Moderate risk of pressure injury',
      riskLevel: 'moderate',
    };
  if (total <= 18)
    return { totalScore: total, interpretation: 'Mild risk of pressure injury', riskLevel: 'low' };
  return { totalScore: total, interpretation: 'No significant risk', riskLevel: 'low' };
}

/** ADL — Katz Index of Independence. Score 0-6 (6=fully independent) */
export function scoreAdl(responses: {
  bathing: 0 | 1;
  dressing: 0 | 1;
  toileting: 0 | 1;
  transferring: 0 | 1;
  continence: 0 | 1;
  feeding: 0 | 1;
}): { totalScore: number; interpretation: string; riskLevel: string } {
  const total =
    responses.bathing +
    responses.dressing +
    responses.toileting +
    responses.transferring +
    responses.continence +
    responses.feeding;
  if (total === 6)
    return { totalScore: total, interpretation: 'Full function (independent)', riskLevel: 'low' };
  if (total >= 4)
    return { totalScore: total, interpretation: 'Moderate impairment', riskLevel: 'moderate' };
  if (total >= 2)
    return { totalScore: total, interpretation: 'Severe functional impairment', riskLevel: 'high' };
  return {
    totalScore: total,
    interpretation: 'Very severe functional impairment',
    riskLevel: 'severe',
  };
}

/** IADL — Lawton Scale. Score 0-8 (8=fully independent) */
export function scoreIadl(responses: {
  telephone: 0 | 1;
  shopping: 0 | 1;
  foodPreparation: 0 | 1;
  housekeeping: 0 | 1;
  laundry: 0 | 1;
  transportation: 0 | 1;
  medications: 0 | 1;
  finances: 0 | 1;
}): { totalScore: number; interpretation: string; riskLevel: string } {
  const total =
    responses.telephone +
    responses.shopping +
    responses.foodPreparation +
    responses.housekeeping +
    responses.laundry +
    responses.transportation +
    responses.medications +
    responses.finances;
  if (total >= 7)
    return { totalScore: total, interpretation: 'High function (independent)', riskLevel: 'low' };
  if (total >= 4)
    return { totalScore: total, interpretation: 'Needs some assistance', riskLevel: 'moderate' };
  if (total >= 2)
    return {
      totalScore: total,
      interpretation: 'Needs considerable assistance',
      riskLevel: 'high',
    };
  return { totalScore: total, interpretation: 'Fully dependent', riskLevel: 'severe' };
}

/** Falls Risk Assessment Tool (FRAT) — Score 0-10+ */
export function scoreFallsRisk(responses: {
  recentFalls: number;
  medications: number;
  psychologicalStatus: number;
  cognitiveStatus: number;
  mobilityAids: number;
}): { totalScore: number; interpretation: string; riskLevel: string } {
  const total =
    responses.recentFalls +
    responses.medications +
    responses.psychologicalStatus +
    responses.cognitiveStatus +
    responses.mobilityAids;
  if (total <= 2) return { totalScore: total, interpretation: 'Low falls risk', riskLevel: 'low' };
  if (total <= 5)
    return { totalScore: total, interpretation: 'Medium falls risk', riskLevel: 'moderate' };
  if (total <= 8)
    return { totalScore: total, interpretation: 'High falls risk', riskLevel: 'high' };
  return { totalScore: total, interpretation: 'Very high falls risk', riskLevel: 'severe' };
}

/** MNA-SF (Mini Nutritional Assessment - Short Form) — Score 0-14 */
export function scoreMna(responses: {
  appetiteDecline: number;
  weightLoss: number;
  mobility: number;
  psychologicalStress: number;
  neuropsychological: number;
  bmi: number;
}): { totalScore: number; interpretation: string; riskLevel: string } {
  const total =
    responses.appetiteDecline +
    responses.weightLoss +
    responses.mobility +
    responses.psychologicalStress +
    responses.neuropsychological +
    responses.bmi;
  if (total >= 12)
    return { totalScore: total, interpretation: 'Normal nutritional status', riskLevel: 'low' };
  if (total >= 8)
    return { totalScore: total, interpretation: 'At risk of malnutrition', riskLevel: 'moderate' };
  return { totalScore: total, interpretation: 'Malnourished', riskLevel: 'severe' };
}

/** Abbey Pain Scale — Score 0-18. For people unable to verbalise pain */
export function scoreAbbeyPain(responses: {
  vocalisation: number;
  facialExpression: number;
  bodyLanguage: number;
  behaviouralChange: number;
  physiologicalChange: number;
  physicalChange: number;
}): { totalScore: number; interpretation: string; riskLevel: string } {
  const total =
    responses.vocalisation +
    responses.facialExpression +
    responses.bodyLanguage +
    responses.behaviouralChange +
    responses.physiologicalChange +
    responses.physicalChange;
  if (total <= 2) return { totalScore: total, interpretation: 'No pain', riskLevel: 'low' };
  if (total <= 7) return { totalScore: total, interpretation: 'Mild pain', riskLevel: 'moderate' };
  if (total <= 13) return { totalScore: total, interpretation: 'Moderate pain', riskLevel: 'high' };
  return { totalScore: total, interpretation: 'Severe pain', riskLevel: 'severe' };
}
